import { Directive } from '@angular/core';
import { IDataModel, LoggerService } from '@klickdata/core/application';
import { ConfigService } from '@klickdata/core/config';
import { Customer, CustomerData } from '@klickdata/core/customer';
import { ResponseData } from '@klickdata/core/http';
import { User, UserData } from '@klickdata/core/user';
import * as Sentry from '@sentry/browser';
import Echo, { Channel } from 'laravel-echo';
import Pusher from 'pusher-js';
import {
    BehaviorSubject,
    EMPTY,
    Observable,
    Subject,
    catchError,
    combineLatest,
    concat,
    defer,
    distinctUntilChanged,
    distinctUntilKeyChanged,
    filter,
    fromEvent,
    interval,
    map,
    merge,
    of,
    pairwise,
    switchMap,
    takeUntil,
    tap
} from 'rxjs';

/** ms of silence before Pusher pings the server; also how long a hidden tab may sleep before we distrust the socket. */
const ACTIVITY_TIMEOUT_MS = 30000;

@Directive()
export abstract class EchoService {
    protected token: BehaviorSubject<string> = new BehaviorSubject(null);
    protected sharedUser = new BehaviorSubject<User>(null);
    protected sharedCustomer = new BehaviorSubject<Customer>(null);

    protected destroy = new Subject<boolean>();
    private echo: Echo;
    private echoReady = new BehaviorSubject<Echo>(null);
    private connectionState = new BehaviorSubject<string>('disconnected');
    private hiddenSince: number | null = null;
    private fallbackPollingReported = false;
    private userChannel: Observable<Channel>;
    private customerChannel: Observable<Channel>;
    private echoConnectionCleanup: (() => void) | null = null;
    private echoReconnected = new Subject<void>();
    private echoAuthFailed = new Subject<void>();

    constructor(protected configService: ConfigService, protected logger: LoggerService) {
        /**
         * Load Echo
         */
        Window.bind('Pusher', Pusher);

        this.userChannel = this.sharedUser.pipe(
            filter((user) => !!user?.id),
            distinctUntilKeyChanged('id'),
            map((user) => this.echo.private(`App.User.${user.id}`))
        );

        this.customerChannel = this.sharedCustomer.pipe(
            filter((customer) => !!customer?.id),
            distinctUntilKeyChanged('id'),
            map((customer) => this.echo.private(`customer.${customer.id}`))
        );

        /**
         * Create the Echo instance only once when the user first logs in.
         * Do NOT recreate it on token refresh — that was causing multiple
         * abandoned WebSocket connections and dead channel subscriptions
         * (especially visible on iPhone Safari after returning from background).
         */
        this.sharedUser.pipe(
            takeUntil(this.destroy),
            pairwise()
        ).subscribe(([prevUser, currUser]) => {
            if (currUser && !prevUser) {
                if (!this.echo) {
                    this.echo = this.getEchoInstance();
                    this.bindEchoConnectionEvents();
                    this.echoReady.next(this.echo);
                }
                this.setEchoAuthToken(this.token.value);
            } else if (!currUser && !this.token.value) {
                this.disconnect();
            }
        });

        /**
         * When the token refreshes, only update the Echo auth header —
         * never recreate the Echo instance. This keeps channel subscriptions
         * alive and the WebSocket stable across token rotations.
         */
        this.token.pipe(
            takeUntil(this.destroy),
            filter(token => !!token && !!this.echo)
        ).subscribe(token => {
            this.setEchoAuthToken(token);
        });

        this.watchBrowserLifecycle();
    }

    /** Emits when Pusher reconnects after a drop (e.g. background tab). */
    protected onEchoReconnected(): Observable<void> {
        return this.echoReconnected.asObservable();
    }

    /** Emits when private channel auth is rejected (e.g. broadcasting/auth 401 after sleep). */
    protected onEchoAuthFailed(): Observable<void> {
        return this.echoAuthFailed.asObservable();
    }

    private bindEchoConnectionEvents(): void {
        this.unbindEchoConnectionEvents();
        const pusher = this.echo?.connector?.pusher;
        if (!pusher) {
            return;
        }

        const onConnected = () => this.echoReconnected.next();
        pusher.connection.bind('connected', onConnected);

        const onStateChange = ({ current }: { current: string }) => this.connectionState.next(current);
        pusher.connection.bind('state_change', onStateChange);
        this.connectionState.next(pusher.connection.state);

        const onGlobal = (eventName: string, data: { status?: number }) => {
            if (eventName === 'pusher:subscription_error' && (data?.status === 401 || data?.status === 403)) {
                this.logger.log('Echo subscription rejected', data?.status);
                this.echoAuthFailed.next();
            }
        };
        pusher.bind_global(onGlobal);

        this.echoConnectionCleanup = () => {
            pusher.connection.unbind('connected', onConnected);
            pusher.connection.unbind('state_change', onStateChange);
            pusher.unbind_global(onGlobal);
        };
    }

    private unbindEchoConnectionEvents(): void {
        this.echoConnectionCleanup?.();
        this.echoConnectionCleanup = null;
    }

    private disconnect() {
        this.unbindEchoConnectionEvents();
        if (this.echo) {
            this.echo.disconnect();
            this.echo = null;
            this.echoReady.next(null);
        }
        this.connectionState.next('disconnected');
    }

    /**
     * Browsers silently kill or freeze the WebSocket of a background tab
     * (iOS Safari on every app switch, Chrome when it freezes or bfcaches a tab).
     * Pusher only notices after activityTimeout + pongTimeout (~40s), so when the
     * tab comes back we check the connection ourselves and reconnect right away.
     */
    private watchBrowserLifecycle(): void {
        fromEvent(document, 'visibilitychange')
            .pipe(takeUntil(this.destroy))
            .subscribe(() => {
                if (!this.isTabVisible()) {
                    this.hiddenSince = Date.now();
                    return;
                }
                const hiddenFor = this.hiddenSince ? Date.now() - this.hiddenSince : 0;
                this.hiddenSince = null;
                this.reconnectIfStale(hiddenFor >= ACTIVITY_TIMEOUT_MS);
            });

        merge(
            fromEvent<PageTransitionEvent>(window, 'pageshow').pipe(
                filter((event) => event.persisted),
                map(() => true)
            ),
            fromEvent(document, 'resume').pipe(map(() => true)),
            fromEvent(window, 'online').pipe(map(() => false))
        )
            .pipe(takeUntil(this.destroy))
            .subscribe((socketMayBeDead) => this.reconnectIfStale(socketMayBeDead));
    }

    /**
     * Connect again unless Pusher is already connecting, or is connected and we have no reason to doubt it.
     * @param socketMayBeDead the tab slept long enough that a "connected" socket may be a dead one.
     */
    private reconnectIfStale(socketMayBeDead: boolean): void {
        const pusher = this.echo?.connector?.pusher;
        if (!pusher || !this.isTabVisible()) {
            return;
        }

        const state = pusher.connection.state;
        if (state === 'connecting' || (state === 'connected' && !socketMayBeDead)) {
            return;
        }

        this.logger.log('Echo reconnecting after tab resume', state);
        if (state === 'connected') {
            pusher.disconnect();
        }
        pusher.connect();
    }

    private isTabVisible(): boolean {
        return document.visibilityState === 'visible';
    }

    /** Emits true while the tab is visible and the socket is not connected — the only time polling is worth it. */
    private socketDownWhileVisible(): Observable<boolean> {
        const tabVisible$ = concat(
            defer(() => of(this.isTabVisible())),
            fromEvent(document, 'visibilitychange').pipe(map(() => this.isTabVisible()))
        );

        return combineLatest([this.connectionState, tabVisible$]).pipe(
            map(([state, visible]) => visible && state !== 'connected'),
            distinctUntilChanged()
        );
    }

    protected getEchoInstance(): Echo {
        const authEndpoint = this.configService.config.WS_ECHO_AUTH;

        return new Echo({
            broadcaster: 'pusher',
            disableStats: true,

            wsHost: this.configService.config.WS_ECHO_SERVER,
            wsPort: this.configService.config.WS_ECHO_PORT,
            wssPort: this.configService.config.WSS_ECHO_PORT,

            key: this.configService.config.WS_ECHO_APP_KEY,
            cluster: this.configService.config.WS_ECHO_CLUSTER,

            forceTLS: this.configService.config.WS_ECHO_TLS,
            encrypted: this.configService.config.WS_ECHO_TLS,
            enabledTransports: ['ws', 'wss'],

            /**
             * Tuned for iOS Safari background/foreground cycles.
             * iOS kills WebSocket connections when a tab is backgrounded — these
             * settings tell Pusher to be patient and keep retrying instead of
             * giving up quickly when the user returns from another app.
             *
             * activityTimeout: ms of silence before Pusher sends a ping (default 120000)
             * pongTimeout:     ms to wait for pong before declaring connection dead (default 30000)
             */
            activityTimeout: ACTIVITY_TIMEOUT_MS,
            pongTimeout: 10000,

            authorizer: (channel: { name: string }) => ({
                authorize: (socketId: string, callback: (error: boolean, data: unknown) => void) => {
                    this.authorizeEchoChannel(authEndpoint, socketId, channel.name, callback);
                },
            }),
        });
    }

    private authorizeEchoChannel(
        authEndpoint: string,
        socketId: string,
        channelName: string,
        callback: (error: boolean, data: unknown) => void
    ): void {
        const token = this.token.value;
        const headers: Record<string, string> = {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        };

        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }

        fetch(authEndpoint, {
            method: 'POST',
            headers,
            body: JSON.stringify({ socket_id: socketId, channel_name: channelName }),
        })
            .then(async (response) => {
                if (response.status === 401 || response.status === 403) {
                    this.logger.log('Echo channel auth rejected', response.status, channelName);
                    this.echoAuthFailed.next();
                    callback(true, { status: response.status });
                    return;
                }

                if (!response.ok) {
                    callback(true, await response.text());
                    return;
                }

                callback(false, await response.json());
            })
            .catch((err) => callback(true, err));
    }

    /**
     * Set token to keep echo auth token updated.
     * @param token
     */
    private setEchoAuthToken(token: string) {
        // this.echo.connector.pusher.config.auth.headers['Authorization'] = `Bearer ${token}`;
        this.echo.connector.options.auth.headers['Authorization'] = `Bearer ${token}`;
    }

    public listen<T>(channel: string, event: string): Observable<T> {
        return this.echoReady.pipe(switchMap((echo) => (echo ? this.listenOn<T>(echo.channel(channel), event) : EMPTY)));
    }

    /**
     * Listen to a private channel. When a looper is given, it backs up the socket:
     * - while the socket is down (and the tab is visible) it polls every looperInterval ms;
     * - when the socket reconnects it runs once to catch up on events missed while it was down.
     * The live channel listener stays attached the whole time, so events arrive again as soon as the socket is back.
     */
    public listenPrivate<T extends IDataModel | IDataModel[]>(
        channel: string,
        event: string,
        looper: Observable<ResponseData<T>> = null,
        looperInterval = 10000
    ): Observable<ResponseData<T>> {
        const live$ = this.echoReady.pipe(
            switchMap((echo) => (echo ? this.listenOn<ResponseData<T>>(echo.private(channel), event) : EMPTY))
        );

        if (!looper) {
            return live$;
        }

        return merge(live$, this.pollWhileSocketDown(channel, looper, looperInterval));
    }

    private pollWhileSocketDown<T extends IDataModel | IDataModel[]>(
        channel: string,
        looper: Observable<ResponseData<T>>,
        looperInterval: number
    ): Observable<ResponseData<T>> {
        const safeLooper = looper.pipe(
            catchError((error) => {
                this.logger.log('Echo looper request failed', channel, error);
                if (!this.isLocalBuild()) {
                    Sentry.addBreadcrumb({ category: 'echo', message: `Polling ${channel} failed`, level: Sentry.Severity.Warning });
                }
                return EMPTY;
            })
        );

        const fallback$ = this.socketDownWhileVisible().pipe(
            switchMap((socketDown) =>
                socketDown
                    ? interval(looperInterval).pipe(
                          tap((tick) => tick === 0 && this.reportFallbackPolling(channel)),
                          switchMap(() => safeLooper)
                      )
                    : EMPTY
            )
        );

        const catchUp$ = this.echoReconnected.pipe(switchMap(() => safeLooper));

        return merge(fallback$, catchUp$).pipe(
            filter((res) => !!res?.data),
            distinctUntilChanged((t1, t2) => this.detectChange(t1.data, t2.data))
        );
    }

    /** Leaves a trail in Sentry each time a screen falls back to polling, and reports it once per page load. */
    private reportFallbackPolling(channel: string): void {
        const state = this.connectionState.value;
        this.logger.log('Echo socket down, polling', channel, state);
        if (this.isLocalBuild()) {
            return;
        }

        Sentry.addBreadcrumb({ category: 'echo', message: `Polling ${channel}`, data: { state } });

        if (!this.fallbackPollingReported) {
            this.fallbackPollingReported = true;
            Sentry.captureMessage('Echo socket down, falling back to polling', {
                level: Sentry.Severity.Warning,
                tags: { echo_state: state },
                extra: { channel },
            });
        }
    }

    private isLocalBuild(): boolean {
        return this.configService.config.buildMode === 'local';
    }

    /** Listen to one event and remove exactly this listener on unsubscribe, so listeners never pile up. */
    private listenOn<T>(channel: Channel, event: string): Observable<T> {
        return new Observable<T>((subs) => {
            const callback = (data: T) => subs.next(data);
            channel.listen(event, callback);
            return () => channel.stopListening(event, callback);
        });
    }

    private detectChange(t1: IDataModel | IDataModel[], t2: IDataModel | IDataModel[]): boolean {
        if (!Array.isArray(t1) && !Array.isArray(t2)) {
            return this.isSameItem(t1, t2);
        }

        if (Array.isArray(t1) && Array.isArray(t2)) {
            return (
                t1.length === t2.length &&
                t1.every((item1) => this.isSameItem(item1, t2.find((item) => item.id === item1.id)))
            );
        }

        return true;
    }

    /**
     * Models with a prompt status (AI items) count as changed only when that status changes.
     * Models without one (payment, quote, occasion...) count as changed when any of their data changes.
     */
    private isSameItem(t1: IDataModel, t2: IDataModel): boolean {
        if (t1?.prompt_status !== undefined || t2?.prompt_status !== undefined) {
            return t1?.prompt_status === t2?.prompt_status;
        }
        return JSON.stringify(t1) === JSON.stringify(t2);
    }

    public get socketId(): string {
        return this.echo?.socketId() ?? '';
    }

    public listenUserChannel<T>(event: string): Observable<T> {
        return this.userChannel.pipe(switchMap((ch) => this.listenOn<T>(ch, event)));
    }

    public listenCustomerChannel<T>(event: string): Observable<T> {
        return this.customerChannel.pipe(switchMap((ch) => this.listenOn<T>(ch, event)));
    }

    /**
     * Auth socket
     */
    protected getAuthUserSocket(): Observable<ResponseData<UserData>> {
        return this.listenUserChannel('UserUpdateEvent').pipe(tap((v) => this.logger.log(v)));
    }

    protected getAuthCustomerSocket(): Observable<ResponseData<CustomerData>> {
        return this.listenCustomerChannel('CustomerUpdateEvent').pipe(tap((v) => this.logger.log(v)));
    }

    public tokenExpiringSocket(): Observable<ResponseData<{ token_expiry: string }>> {
        return this.listenUserChannel('TokenExpiringEvent');
    }

    protected tokenExpiredSocket(): Observable<ResponseData<any>> {
        return this.listenUserChannel('TokenExpiredEvent');
    }

    protected logOutOtherSocket(): Observable<ResponseData<any>> {
        return this.listenUserChannel('LogOutOtherEvent');
    }

    public getAuthReportZipDownloadSocket(): Observable<ResponseData<{ download_url: string, file_name: string }>> {
        return this.listenUserChannel('UserZipReportDownload').pipe(tap((v) => this.logger.log(v)));
    }
}
